/**
 * Analytics Charts Component
 *
 * Provides data visualizations for analytics data:
 * - Time series line chart
 * - Top event types bar chart
 * - Endpoint performance table
 */

import { format, parseISO } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { TimeSeriesDataPoint, TopEventType, EndpointPerformance } from '@/types/analytics';
import { AUDIT_LOG_CATEGORY_LABELS, AUDIT_LOG_CATEGORY_COLORS } from '@/types/audit-log';

/**
 * Time Series Chart Props
 */
interface TimeSeriesChartProps {
  data: TimeSeriesDataPoint[];
}

/**
 * Time Series Chart
 * Simple line chart showing events over time
 */
export function TimeSeriesChart({ data }: TimeSeriesChartProps) {
  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        No time series data available
      </div>
    );
  }

  // Chart dimensions
  const width = 800;
  const height = 300;
  const padding = { top: 20, right: 40, bottom: 60, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Find min/max values
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const minCount = 0;

  // Scale functions
  const scaleX = (index: number) => (index / (data.length - 1 || 1)) * chartWidth;
  const scaleY = (count: number) =>
    chartHeight - ((count - minCount) / (maxCount - minCount || 1)) * chartHeight;

  // Generate path for line
  const linePath = data
    .map((point, index) => {
      const x = scaleX(index);
      const y = scaleY(point.count);
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    })
    .join(' ');

  // Y-axis ticks
  const yTicks = 5;
  const yTickValues = Array.from({ length: yTicks }, (_, i) => {
    const value = minCount + ((maxCount - minCount) / (yTicks - 1)) * i;
    return Math.round(value);
  });

  // X-axis tick indices (show max 8 labels)
  const maxXTicks = Math.min(8, data.length);
  const xTickIndices =
    data.length <= maxXTicks
      ? data.map((_, i) => i)
      : Array.from({ length: maxXTicks }, (_, i) =>
          Math.floor((i * (data.length - 1)) / (maxXTicks - 1))
        );

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ minWidth: '600px' }}>
        <g transform={`translate(${padding.left},${padding.top})`}>
          {/* Y-axis */}
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={chartHeight}
            stroke="currentColor"
            strokeWidth={1}
            className="text-gray-300"
          />

          {/* Y-axis ticks and labels */}
          {yTickValues.map(value => {
            const y = scaleY(value);
            return (
              <g key={value}>
                <line
                  x1={-5}
                  y1={y}
                  x2={chartWidth}
                  y2={y}
                  stroke="currentColor"
                  strokeWidth={1}
                  className="text-gray-200"
                  strokeDasharray="3,3"
                />
                <text
                  x={-10}
                  y={y}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="text-xs fill-gray-600"
                >
                  {value}
                </text>
              </g>
            );
          })}

          {/* X-axis */}
          <line
            x1={0}
            y1={chartHeight}
            x2={chartWidth}
            y2={chartHeight}
            stroke="currentColor"
            strokeWidth={1}
            className="text-gray-300"
          />

          {/* X-axis labels */}
          {xTickIndices.map(index => {
            const point = data[index];
            const x = scaleX(index);
            const label = format(parseISO(point.timestamp), 'MMM d HH:mm');
            return (
              <text
                key={index}
                x={x}
                y={chartHeight + 20}
                textAnchor="middle"
                className="text-xs fill-gray-600"
                transform={`rotate(-45 ${x} ${chartHeight + 20})`}
              >
                {label}
              </text>
            );
          })}

          {/* Line path */}
          <path
            d={linePath}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="text-blue-600"
          />

          {/* Data points */}
          {data.map((point, index) => {
            const x = scaleX(index);
            const y = scaleY(point.count);
            return (
              <g key={index}>
                <circle cx={x} cy={y} r={4} fill="currentColor" className="text-blue-600" />
                <title>
                  {format(parseISO(point.timestamp), 'PPpp')}: {point.count} events
                </title>
              </g>
            );
          })}
        </g>

        {/* Y-axis label */}
        <text
          x={padding.left - 45}
          y={height / 2}
          textAnchor="middle"
          transform={`rotate(-90 ${padding.left - 45} ${height / 2})`}
          className="text-sm font-medium fill-gray-700"
        >
          Event Count
        </text>

        {/* X-axis label */}
        <text
          x={width / 2}
          y={height - 5}
          textAnchor="middle"
          className="text-sm font-medium fill-gray-700"
        >
          Time
        </text>
      </svg>
    </div>
  );
}

/**
 * Top Event Types Chart Props
 */
interface TopEventTypesChartProps {
  data: TopEventType[];
}

/**
 * Top Event Types Chart
 * Horizontal bar chart showing most frequent event types
 */
export function TopEventTypesChart({ data }: TopEventTypesChartProps) {
  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        No event type data available
      </div>
    );
  }

  // Take top 10
  const topData = data.slice(0, 10);
  const maxCount = Math.max(...topData.map(d => d.count), 1);

  return (
    <div className="space-y-3">
      {topData.map((item, index) => {
        const percentage = (item.count / maxCount) * 100;
        return (
          <div key={index} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="font-mono text-xs truncate" title={item.eventType}>
                  {item.eventType}
                </span>
                <Badge
                  variant="outline"
                  className={`${AUDIT_LOG_CATEGORY_COLORS[item.category]} text-xs`}
                >
                  {AUDIT_LOG_CATEGORY_LABELS[item.category]}
                </Badge>
              </div>
              <span className="font-semibold ml-2">{item.count.toLocaleString()}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Response Times Table Props
 */
interface ResponseTimesTableProps {
  data: EndpointPerformance[];
  /** Callback when an endpoint row is clicked */
  onEndpointClick?: (endpoint: string) => void;
}

/**
 * Response Times Table
 * Shows average response times and error rates by endpoint
 * Supports click-through to view individual requests
 */
export function ResponseTimesTable({ data, onEndpointClick }: ResponseTimesTableProps) {
  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        No endpoint performance data available
      </div>
    );
  }

  // Sort by average response time (slowest first)
  const sortedData = [...data].sort((a, b) => b.avgMs - a.avgMs);

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Endpoint</TableHead>
            <TableHead className="text-right">Avg Time (ms)</TableHead>
            <TableHead className="text-right">Requests</TableHead>
            <TableHead className="text-right">Error Rate</TableHead>
            {onEndpointClick && <TableHead className="w-10"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((item, index) => {
            const errorRatePercent = item.errorRate.toFixed(1);
            const isHighErrorRate = item.errorRate >= 20;
            const isSlowEndpoint = item.avgMs >= 1000; // >= 1s

            return (
              <TableRow
                key={index}
                className={
                  onEndpointClick ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''
                }
                onClick={() => onEndpointClick?.(item.endpoint)}
              >
                <TableCell className="font-mono text-xs max-w-xs truncate" title={item.endpoint}>
                  {item.endpoint}
                </TableCell>
                <TableCell
                  className={`text-right font-medium ${isSlowEndpoint ? 'text-orange-600' : ''}`}
                >
                  {item.avgMs.toFixed(0)}
                </TableCell>
                <TableCell className="text-right">{item.requestCount.toLocaleString()}</TableCell>
                <TableCell className="text-right">
                  <span
                    className={`font-medium ${isHighErrorRate ? 'text-red-600' : 'text-green-600'}`}
                  >
                    {errorRatePercent}%
                  </span>
                </TableCell>
                {onEndpointClick && (
                  <TableCell className="text-right">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
